import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, TextArea, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { Chip } from '@/components/Chip'
import { PhotoAttachInput } from '@/components/PhotoAttachInput'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { toLocalInputValue } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'
import { uploadAttachment } from '@/lib/attachments'
import { correctionEventType, payloadValue, useEditableEvent } from '@/lib/editEvent'

type FeedType = 'bottle_formula' | 'bottle_breastmilk' | 'breast_left' | 'breast_right' | 'both' | 'solid'

const TYPES: { key: FeedType; label: string }[] = [
  { key: 'bottle_formula', label: 'Bottle · formula' },
  { key: 'bottle_breastmilk', label: 'Bottle · breast milk' },
  { key: 'breast_left', label: 'Breast · left' },
  { key: 'breast_right', label: 'Breast · right' },
  { key: 'both', label: 'Both breasts' },
  { key: 'solid', label: 'Solid food' },
]

export function FeedLoggerScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const edit = useEditableEvent(['early_care.feeding.recorded', 'early_care.feeding.corrected'])

  const [type, setType] = useState<FeedType>('bottle_formula')
  const [volumeMl, setVolumeMl] = useState<string>('')
  const [durationMin, setDurationMin] = useState<string>('')
  const [occurredAt, setOccurredAt] = useState<string>(toLocalInputValue(new Date()))
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [prefilled, setPrefilled] = useState(false)

  // Prefill form when we're editing an existing event.
  useEffect(() => {
    if (!edit.original || prefilled) return
    const p = edit.original.payload
    const ft = payloadValue<string>(p, 'feed_type') as FeedType | undefined
    if (ft) setType(ft)
    const vol = payloadValue<number>(p, 'volume_ml')
    if (vol != null) setVolumeMl(String(vol))
    const dur = payloadValue<number>(p, 'duration_minutes')
    if (dur != null) setDurationMin(String(dur))
    const n = payloadValue<string>(p, 'note')
    if (n) setNote(n)
    setOccurredAt(toLocalInputValue(new Date(edit.original.occurred_at)))
    setPrefilled(true)
  }, [edit.original, prefilled])

  if (!person || !familyId || !user) return <Screen title="Log feed" back />

  const showVolume = type.startsWith('bottle_')
  const showDuration = type.startsWith('breast_') || type === 'both'
  const packColor = PACK_COLORS.early_care.light

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      if (!person || !familyId || !user) return

      const payload: Record<string, unknown> = { feed_type: type }
      if (showVolume) {
        const ml = parseFloat(volumeMl)
        if (Number.isNaN(ml) || ml <= 0 || ml > 500) throw new Error('Enter a volume between 1 and 500 mL')
        payload.volume_ml = ml
      }
      if (showDuration) {
        const min = parseInt(durationMin, 10)
        if (Number.isNaN(min) || min <= 0 || min > 180) throw new Error('Enter duration between 1 and 180 minutes')
        payload.duration_minutes = min
      }
      if (note.trim()) payload.note = note.trim()

      const eventType = edit.original
        ? (correctionEventType(edit.original.event_type) as 'early_care.feeding.corrected')
        : 'early_care.feeding.recorded'

      if (photos.length > 0) payload.has_attachment = true

      const eventId = await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType,
        payload: payload as import('@/lib/types').JSONValue,
        occurredAt: new Date(occurredAt),
        supersedes: edit.supersedesId,
      })

      // Upload attachments AFTER event insert so each row has a valid event_id FK
      for (const file of photos) {
        await uploadAttachment({ familyId, userId: user.id, file, eventId })
      }

      if (edit.original) nav('/timeline'); else nav(-1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const isEditing = !!edit.original
  return (
    <Screen title={isEditing ? 'Correct feed' : 'Log feed'} back>
      <form onSubmit={save} className="px-5 py-5 space-y-6">
        {edit.loading && <div className="text-caption text-text-tertiary">Loading original entry…</div>}
        {edit.error && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {edit.error}
          </div>
        )}
        {isEditing && (
          <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-3 text-caption text-text-secondary">
            Correcting an earlier feed. Saving creates a new entry that supersedes the original — the original stays in the record as history.
          </div>
        )}

        <div>
          <Kicker>Type</Kicker>
          <div className="mt-3 flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <Chip
                key={t.key}
                color={packColor}
                emphasis={type === t.key ? 'filled' : 'quiet'}
                onClick={() => setType(t.key)}
              >
                {t.label}
              </Chip>
            ))}
          </div>
        </div>

        {showVolume && (
          <div>
            <Kicker>Volume</Kicker>
            <div className="mt-2 flex gap-2">
              {[30, 60, 90, 120, 150].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVolumeMl(String(v))}
                  className="flex-1 rounded-r-sm px-2 py-2.5 text-caption font-medium border border-divider-subtle bg-bg-elevated text-text-secondary active:scale-95 transition-transform"
                >
                  {v} mL
                </button>
              ))}
            </div>
            <div className="mt-2">
              <Field label="Custom (mL)">
                <TextInput
                  type="number"
                  inputMode="decimal"
                  min={1}
                  max={500}
                  value={volumeMl}
                  onChange={(e) => setVolumeMl(e.target.value)}
                  placeholder="e.g. 120"
                />
              </Field>
            </div>
          </div>
        )}

        {showDuration && (
          <div>
            <Kicker>Duration</Kicker>
            <div className="mt-2 flex gap-2">
              {[5, 10, 15, 20, 30].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDurationMin(String(m))}
                  className="flex-1 rounded-r-sm px-2 py-2.5 text-caption font-medium border border-divider-subtle bg-bg-elevated text-text-secondary active:scale-95 transition-transform"
                >
                  {m}m
                </button>
              ))}
            </div>
            <div className="mt-2">
              <Field label="Custom (minutes)">
                <TextInput
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={180}
                  value={durationMin}
                  onChange={(e) => setDurationMin(e.target.value)}
                />
              </Field>
            </div>
          </div>
        )}

        <Field label="When">
          <TextInput type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        </Field>

        <Field label="Note (optional)">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to remember?" />
        </Field>

        <PhotoAttachInput
          photos={photos}
          onChange={setPhotos}
          label="Photos (optional)"
          hint="Bottle shot, baby mid-feed, spit-up aftermath — anything worth keeping."
          compact
        />

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" packColor={packColor} loading={busy} className="w-full">
          {isEditing ? 'Save correction' : 'Save feed'}
        </Button>
      </form>
    </Screen>
  )
}
